import { formatDriverForUpdate, useDriverManagement } from '@/api/useDriverManagement'
import { invalidateTypedQueries } from '@/hooks/useApiClient'
import type { components } from '@/types/api/v1'
import api from '@/utils/api'
import { dayjsWithTimezone } from '@/utils/intl/dateTimeFormatters'
import { HomeOutlined, MoonOutlined, SunOutlined } from '@ant-design/icons'
import { Horizontal, Texto } from '@gravitate-js/excalibrr'
import { useQueryClient } from '@tanstack/react-query'
import type { ICellRendererParams } from 'ag-grid-community'
import { Dropdown, Flex } from 'antd'
import { useCallback } from 'react'
import { ScheduleTemplateEditor, type ScheduleTemplateValue } from '../../Manage/GridColumns/ScheduleTemplateEditor'
import { useScheduleItems } from '../Context/ScheduleItems/ScheduleItemsContext'
import { InfoCellRenderer } from '../Grid/CellRenderers/InfoCellRenderer'
import { getDriverShiftStart } from '../utils/getDefaultShift'
type Data = components['schemas']['DriverInfoRow']
const getDriverPreferredTime = (data: components['schemas']['DriverInfoRow']) => {
  const shift = getDriverShiftStart(data)
  if (!shift) return '--:--  '
  return shift.format('h:mma z')
}

export const getHourFromShiftStartTime = ({
  am_shift,
  pm_shift,
  shift_preference,
}: components['schemas']['DriverInfoRow']) => {
  if (!shift_preference) return 0
  const shift = shift_preference === 'am' ? am_shift : pm_shift
  if (!shift) return 0
  return dayjsWithTimezone(shift.start_time, shift.timezone_data?.depot as string | undefined).hour()
}

const DepotTag = ({ color, title }: { color?: string; title?: string | null }) => (
  <Horizontal gap={4} alignItems='center'>
    <HomeOutlined style={{ color }} /> <Texto style={{ color }}>{title ?? 'No location'}</Texto>
  </Horizontal>
)

const Action = ({ data }: { data: Data }) => {
  if (!data.shift_preference) return null
  return (
    <Flex gap={4}>
      {data.shift_preference === 'am' ? <SunOutlined /> : data.shift_preference === 'pm' ? <MoonOutlined /> : null}
      {getDriverPreferredTime(data)}
    </Flex>
  )
}

const CarrierInfoCell = (data: components['schemas']['DriverInfoRow']) => (
  <InfoCellRenderer
    title={data.name}
    subtitle={<DepotTag title={data.depot} color='var(--theme-color-4)' />}
    action={<Action data={data} />}
    tag={
      <Texto category='label' className='carrier-color'>
        {`#${data.carrier_resource_num}`}
      </Texto>
    }
  />
)

const DriverInfoCell = (data: components['schemas']['DriverInfoRow']) => {
  const { editTemplateForDriver } = useScheduleItems()
  const { useDriverUpdate } = useDriverManagement()
  const { mutateAsync } = useDriverUpdate()
  const queryClient = useQueryClient()
  const handleAddTemplate = useCallback(
    (driver: Data, value: ScheduleTemplateValue) => {
      mutateAsync(formatDriverForUpdate({ ...driver, ...value }))
        .then(() => {
          if (value.preferred_template_id) {
            return api.autoGenerateSchedules(driver.id)
          }
        })
        .then(() => {
          invalidateTypedQueries(queryClient, 'post', '/driver/schedule/overview')
        })
    },
    [mutateAsync, queryClient]
  )
  return (
    <Dropdown
      trigger={['click']}
      menu={{
        items: data.preferred_template
          ? [
              {
                key: 'edit',
                label: 'Edit Template',
                onClick: () => editTemplateForDriver?.(data?.id ?? null),
              },
            ]
          : [
              {
                key: 'add',
                label: (
                  <ScheduleTemplateEditor driver={data} onChange={handleAddTemplate}>
                    <div style={{ width: '100%' }}>Add Template</div>
                  </ScheduleTemplateEditor>
                ),
              },
            ],
      }}
    >
      <InfoCellRenderer
        title={data.name}
        subtitle={<DepotTag title={data.depot} />}
        action={<Action data={data} />}
        tag={
          <Flex flex='1 0 auto' justify='flex-end' gap={4} align='center'>
            <Texto appearance='hint' category='label'>
              {data.preferred_template ?? 'No Template'}
            </Texto>
          </Flex>
        }
      />
    </Dropdown>
  )
}

export function DriverInfoCellRenderer({ data }: ICellRendererParams<components['schemas']['DriverInfoRow']>) {
  if (!data) return null

  return data.carrier_id ? <CarrierInfoCell {...data} /> : <DriverInfoCell {...data} />
}
